'use client';

import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '@koda/koda-components/atoms/Button';
import { LICENSES_BASE_SLUGS } from '@koda/koda-utils/constants/licenses';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { MainLocale } from '../../../../i18n/constants';
import { PURCHASE_INQUIRY_ROUTE, StepRoute } from '../../../../i18n/pathnames/licenses';
import { FlowController } from '../[licenseType]/FlowController';
import { type LicenseFlowTypeValue } from '../[licenseType]/flows';
import { useProgressValidator } from '../[licenseType]/hooks/useProgressValidator';
import { useLicenseStore } from '../[licenseType]/licenseStore';
import type { SanityLicense } from '../[licenseType]/types/licenses';
import { submitPurchaseInquiry } from './submitPurchaseInquiry';
import { useCurrentStepRoute } from './useCurrentStepRoute';

const CONTENT_PATH = 'licenses.components.navigationButton';

type SubmitResult = { ok: boolean; error?: string };

function isPurchaseInquiryRoute(currentStepRoute: StepRoute | null, nextStepRoute: StepRoute | null): boolean {
  return currentStepRoute == PURCHASE_INQUIRY_ROUTE || nextStepRoute == PURCHASE_INQUIRY_ROUTE;
}

function getNextRoute(flowController: FlowController, currentStepRoute: StepRoute, locale: MainLocale) {
  try {
    return flowController.getAbsoluteRouteInDirection({
      currentStepRoute: currentStepRoute,
      direction: 1,
      locale: locale as MainLocale,
    });
  } catch {
    return `/${locale}/${LICENSES_BASE_SLUGS[locale]}`;
  }
}

/**
 * Derives the current license flow step from the pathname and provides a Next button.
 * Assumes routes follow: /{locale}/licenses/{licenseType}[/{stepRoute}]
 * Where root (no trailing segment) maps to stepRoute '/'.
 */
export const NavigateNextButton = ({
  license,
  text,
  className,
}: {
  license: SanityLicense;
  text?: string;
  className?: string;
}) => {
  const currentStepRoute = useCurrentStepRoute();
  const { canContinue } = useProgressValidator(license);
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations(CONTENT_PATH);
  const flowController = new FlowController(license);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tryNavigateNext = useCallback(async () => {
    if (!currentStepRoute) return;
    const flowController = new FlowController(license);
    let nextRoute: string | undefined;
    try {
      nextRoute = flowController.getAbsoluteRouteInDirection({
        currentStepRoute,
        direction: 1,
        locale: locale as MainLocale,
      });
    } catch {
      nextRoute = `/${locale}/${LICENSES_BASE_SLUGS[locale as MainLocale]}`;
    }
    // Toggle error visibility when invalid attempt
    const isValid = canContinue && !!nextRoute;
    useLicenseStore.getState().setShowErrors(!isValid);

    if (!isValid) {
      requestAnimationFrame(() => {
        const firstError = document.querySelector('[data-error-field]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      return;
    }

    const nextStep = flowController.getNextRouteStep(currentStepRoute, 1);
    const isPurchaseInquiry = isPurchaseInquiryRoute(currentStepRoute, nextStep?.stepRoute || null);

    // If there is a next step, navigate to it
    if (nextRoute && !isPurchaseInquiry) {
      router.push(nextRoute);
      return;
    }

    if (isPurchaseInquiry) {
      // If we are on the purchase inquiry step - call purchase-inquiry route
      setIsSubmitting(true);
      try {
        const response: SubmitResult = await submitPurchaseInquiry({
          kodaMailbox: license.kodaMailbox || '',
          licenseSlug: license.slug?.current || '',
          flowType: license.flowType as LicenseFlowTypeValue,
        });

        // On success, navigate to receipt page
        if (response.ok && response.error === undefined) {
          const receiptRoute = getNextRoute(flowController, PURCHASE_INQUIRY_ROUTE, locale as MainLocale);
          router.push(receiptRoute);
          return;
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [canContinue, currentStepRoute, locale, license, router]);

  if (!currentStepRoute) return null;
  const nextRoute = getNextRoute(flowController, currentStepRoute, locale as MainLocale);

  if (!license || !currentStepRoute || !nextRoute) {
    return null; // Missing context; nothing to render.
  }

  return (
    <Button
      buttonType="button"
      onClick={tryNavigateNext}
      disabled={!nextRoute || isSubmitting}
      loading={isSubmitting}
      variant="primaryDark"
      className={className}
      testId="license-next-button"
    >
      {text ?? t('next')}
    </Button>
  );
};

export const NavigatePreviousButton = ({ license }: { license: SanityLicense }) => {
  const currentStepRoute = useCurrentStepRoute();
  const locale = useLocale();
  const t = useTranslations(CONTENT_PATH);

  if (!license || !currentStepRoute) {
    return null; // Missing context; nothing to render.
  }

  const flowController = new FlowController(license);
  let previousRoute: string;
  try {
    previousRoute =
      flowController.getAbsoluteRouteInDirection({ currentStepRoute, direction: -1, locale: locale as MainLocale }) ??
      `/${locale}/${LICENSES_BASE_SLUGS[locale as MainLocale]}`;
  } catch {
    previousRoute = `/${locale}/${LICENSES_BASE_SLUGS[locale as MainLocale]}`;
  }

  return (
    <Link href={{ pathname: previousRoute }} className="inline-flex items-center gap-2 text-primary underline">
      <ChevronLeftIcon className="size-5" aria-hidden="true" />
      <span>{t('back')}</span>
    </Link>
  );
};
